"use client";

import { useState } from "react";
import type { FormEvent } from "react";
import type { Locale } from "@/i18n/dictionaries";
import styles from "./customJewelry.module.css";

const labels = {
  "en-US": {
    title: "Request a custom quote",
    intro: "Tell us what you want to make. A clear brief helps us quote faster.",
    name: "Name",
    email: "Email",
    phone: "Phone / WhatsApp",
    country: "Country / Region",
    projectType: "Project type",
    material: "Preferred material",
    ringSize: "Size / dimensions",
    budget: "Budget",
    timeline: "Target timeline",
    referenceUrl: "Reference link",
    message: "Project details",
    consent: "You may email me about this quote request.",
    submit: "Submit request",
    submitting: "Submitting...",
    success: "Request received. We will contact you by email.",
    unavailable: "The custom inquiry service is temporarily unavailable.",
    invalid: "Please check the highlighted fields.",
    placeholderMessage: "Example: I want a silver signet ring with black plating, US size 8, based on the reference link. I need 3 samples first...",
    options: {
      projectType: ["Ring", "Pendant", "Earrings", "Bracelet", "Charm", "Small batch", "Other"],
      material: ["925 Sterling Silver", "18K Gold", "14K Gold", "Brass", "Plated finish", "Not sure"],
      budget: ["Under $200", "$200 - $500", "$500 - $1,000", "$1,000+", "Need quote"],
      timeline: ["Flexible", "2 - 4 weeks", "1 - 2 months", "Urgent"]
    }
  },
  "zh-CN": {
    title: "提交定制询盘",
    intro: "请尽量写清楚定制需求，信息越完整，报价越快。",
    name: "姓名",
    email: "邮箱",
    phone: "电话 / WhatsApp",
    country: "国家 / 地区",
    projectType: "项目类型",
    material: "偏好材质",
    ringSize: "尺寸 / 戒号",
    budget: "预算",
    timeline: "期望交期",
    referenceUrl: "参考链接",
    message: "项目说明",
    consent: "允许我们通过邮件联系我确认该定制需求。",
    submit: "提交需求",
    submitting: "提交中...",
    success: "定制需求已收到，我们会通过邮件联系你。",
    unavailable: "定制询盘服务暂时不可用。",
    invalid: "请检查表单内容。",
    placeholderMessage: "例如：我想做一枚银质黑色电镀戒指，US 8 号，参考链接中的造型，先做 3 个样品……",
    options: {
      projectType: ["戒指", "吊坠", "耳饰", "手链", "挂饰", "小批量", "其他"],
      material: ["925 银", "18K 金", "14K 金", "黄铜", "电镀效果", "暂不确定"],
      budget: ["$200 以下", "$200 - $500", "$500 - $1,000", "$1,000+", "需要报价"],
      timeline: ["时间灵活", "2 - 4 周", "1 - 2 个月", "加急"]
    }
  }
} as const;

type SubmitState = {
  status: "idle" | "submitting" | "success" | "error";
  message: string;
};

export function CustomJewelryForm({ locale }: Readonly<{ locale: Locale }>) {
  const t = labels[locale];
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload = Object.fromEntries(data.entries()) as Record<string, string>;
    setState({ status: "submitting", message: "" });

    const response = await fetch("/api/cross-border/inquiries", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...payload,
        locale,
        marketingConsent: data.get("marketingConsent") === "on"
      })
    }).catch(() => null);
    const result = await response?.json().catch(() => null) as { ok?: boolean; message?: string; status?: string } | null;

    if (response?.ok && result?.ok) {
      form.reset();
      setState({ status: "success", message: result.message || t.success });
      return;
    }

    setState({
      status: "error",
      message: result?.message || (result?.status === "invalid" ? t.invalid : t.unavailable)
    });
  }

  return (
    <section className={styles.formSection} id="request-quote">
      <div className={styles.formIntro}>
        <h2>{t.title}</h2>
        <p>{t.intro}</p>
      </div>
      <form className={styles.form} onSubmit={submit}>
        <label>
          <span>{t.name} *</span>
          <input name="name" required minLength={2} maxLength={120} autoComplete="name" />
        </label>
        <label>
          <span>{t.email} *</span>
          <input name="email" required type="email" maxLength={160} autoComplete="email" />
        </label>
        <label>
          <span>{t.phone}</span>
          <input name="phone" maxLength={80} autoComplete="tel" />
        </label>
        <label>
          <span>{t.country}</span>
          <input name="country" maxLength={120} autoComplete="country-name" />
        </label>
        <SelectField name="projectType" label={t.projectType} options={t.options.projectType} />
        <SelectField name="material" label={t.material} options={t.options.material} />
        <label>
          <span>{t.ringSize}</span>
          <input name="ringSize" maxLength={80} placeholder={locale === "en-US" ? "US 7 / EU 54 / 18 mm..." : "US 7 / 港号 14 / 18mm..."} />
        </label>
        <SelectField name="budget" label={t.budget} options={t.options.budget} />
        <SelectField name="timeline" label={t.timeline} options={t.options.timeline} />
        <label>
          <span>{t.referenceUrl}</span>
          <input name="referenceUrl" type="url" maxLength={500} placeholder="https://" />
        </label>
        <label className={styles.full}>
          <span>{t.message} *</span>
          <textarea name="message" required minLength={20} maxLength={4000} rows={7} placeholder={t.placeholderMessage} />
        </label>
        <label className={styles.consent}>
          <input name="marketingConsent" type="checkbox" defaultChecked />
          <span>{t.consent}</span>
        </label>
        <div className={styles.submitRow}>
          <button disabled={state.status === "submitting"} type="submit">
            {state.status === "submitting" ? t.submitting : t.submit}
          </button>
          {state.message ? <p className={state.status === "success" ? styles.success : styles.error}>{state.message}</p> : null}
        </div>
      </form>
    </section>
  );
}

function SelectField({ name, label, options }: Readonly<{ name: string; label: string; options: readonly string[] }>) {
  return (
    <label>
      <span>{label}</span>
      <select name={name} defaultValue="">
        <option value="">—</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}
